import { type KeyboardEvent, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { type CardRef, CHECK_LABELS, changeAuthors, groupByParagraph, isEmptyArtifacts, pidLabel } from "../chat/artifacts";
import type { ChatController, ChatState, Turn } from "../chat/controller";
import { modeLabel } from "../chat/trackingJournal";
import { CommentCard, CompareCard, FindingCard, InsertCard } from "./Cards";
import { EditCard } from "./EditCard";
import { Markdown } from "./Markdown";
import { SaveVersionForm, VersionsMenu } from "./Versions";

export interface QuickAction {
  label: string;
  prompt: string;
  soon?: boolean;
  /** Opens the saved-versions picker instead of filling the composer directly. */
  pick?: "version";
}

export const QUICK_ACTIONS: readonly QuickAction[] = [
  {
    label: "Review against a playbook",
    prompt: "Review this document against my contract playbook. Comment on each issue and propose tracked-change edits where the playbook calls for them.",
  },
  { label: "Redline my selection", prompt: "Redline the selected text: " },
  { label: "Check citations", prompt: "", soon: true },
  {
    label: "Clean-up check",
    prompt: "Run a clean-up check on this document: defined terms, cross-references, numbering, party names, dates, amounts and placeholders.",
  },
  {
    label: "Explain this clause",
    prompt: "Explain the selected clause in plain English: what it does, who it favors, and anything unusual about it.",
  },
  { label: "Compare with…", prompt: "", pick: "version" },
];

const LOAD_ERRORS: Readonly<Record<string, string>> = {
  missing: "the upload was not found",
  too_large: "the document is larger than Ella can read",
  invalid: "the upload was not readable",
  mismatch: "the upload did not match this message",
  not_minted: "the upload was not issued for this account",
  unavailable: "storage was unavailable",
};

function fixPrompt(check: string, message: string, pids: readonly string[]): string {
  const where = pids.slice(0, 6).map((p) => `¶${p}`).join(", ");
  return `Fix this clean-up finding (${CHECK_LABELS[check] ?? check}${where ? `, ${where}` : ""}): ${message} Propose tracked-change edits.`;
}

// ---- composer ------------------------------------------------------------------------------------------------

/** Text another part of the panel puts in the composer (`n` makes a repeat of the same text land again). */
export interface Prefill {
  text: string;
  n: number;
}

function Composer({
  busy,
  disabled,
  prefill,
  onSend,
  onPickVersion,
}: {
  busy: boolean;
  disabled: boolean;
  prefill: Prefill | null;
  onSend: (text: string) => void;
  onPickVersion: () => void;
}) {
  const [text, setText] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  const canSend = !busy && !disabled && text.trim().length > 0;

  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  useEffect(() => {
    if (prefill !== null) fill(prefill.text);
  }, [prefill]);

  function fill(value: string): void {
    setText(value);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (el === null) return;
      el.focus();
      el.setSelectionRange(value.length, value.length);
    });
  }

  function send(): void {
    if (!canSend) return;
    onSend(text);
    setText("");
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  }

  function pick(action: QuickAction): void {
    if (action.pick === "version") onPickVersion();
    else fill(action.prompt);
  }

  return (
    <div className="composer">
      <div className="chips" role="group" aria-label="Quick actions">
        {QUICK_ACTIONS.map((a) => (
          <button
            key={a.label}
            type="button"
            className="chip"
            disabled={a.soon || busy || disabled}
            title={a.soon ? "Coming soon" : a.pick === "version" ? "Pick a saved version to compare with" : a.prompt}
            onClick={() => pick(a)}
          >
            {a.label}
            {a.soon ? <span className="chip__soon">Soon</span> : null}
          </button>
        ))}
      </div>
      <div className="composer__box">
        <textarea
          ref={ref}
          className="composer__input"
          aria-label="Message Ella"
          placeholder={disabled ? "Open this pane inside Word to ask Ella." : "Ask about this document, or tell Ella what to change…"}
          rows={1}
          value={text}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button type="button" className="composer__send" aria-label="Send" disabled={!canSend} onClick={send}>
          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
            <path d="M10 16V4M4.5 9.5 10 4l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <p className="composer__note">Ella reads the document when you send. Nothing changes in Word until you apply it.</p>
    </div>
  );
}

// ---- artifacts -----------------------------------------------------------------------------------------------

function ArtifactsView({ controller, state, turn }: { controller: ChatController; state: ChatState; turn: Turn }) {
  const artifacts = turn.artifacts;
  const grouped = useMemo(() => (artifacts ? groupByParagraph(artifacts, turn.snapshot) : null), [artifacts, turn.snapshot]);
  if (artifacts === null || grouped === null) return null;
  if (artifacts.error) {
    return (
      <p className="banner">
        Ella couldn’t read the document this time ({LOAD_ERRORS[artifacts.error] ?? "it could not be loaded"}). Send your message again.
      </p>
    );
  }
  if (isEmptyArtifacts(artifacts)) return null;

  const applyAll = state.applyAll[turn.id];
  const running = applyAll?.running ?? false;
  const pendingEdits = artifacts.edits.filter((e) => {
    const s = controller.card(turn.id, e.edit_id).status.state;
    return s === "idle" || s === "failed";
  }).length;
  const counts = [
    artifacts.edits.length ? `${artifacts.edits.length} edit${artifacts.edits.length === 1 ? "" : "s"}` : "",
    artifacts.comments.length ? `${artifacts.comments.length} comment${artifacts.comments.length === 1 ? "" : "s"}` : "",
    artifacts.inserts.length ? `${artifacts.inserts.length} draft${artifacts.inserts.length === 1 ? "" : "s"}` : "",
  ].filter(Boolean);

  const renderCard = (card: CardRef) => {
    const cs = controller.card(turn.id, card.id);
    const jump = () => void controller.jump(card.pid, card.kind === "insert" ? undefined : card.item.matched_text || card.item.quote);
    if (card.kind === "edit") {
      return (
        <EditCard
          key={card.id}
          edit={card.item}
          status={cs.status}
          includeReason={cs.includeReason}
          trackingMode={state.trackingMode}
          pendingAuthors={changeAuthors(turn.snapshot, card.pid)}
          locked={running}
          onApply={(mode) => void controller.applyEdit(turn.id, card.id, mode)}
          onDismiss={() => controller.dismiss(turn.id, card.id)}
          onRestore={() => controller.restore(turn.id, card.id)}
          onToggleReason={(v) => controller.setIncludeReason(turn.id, card.id, v)}
          onJump={jump}
          commentRetry={cs.retryComment ? { busy: cs.commentBusy === true, onRetry: () => void controller.retryComment(turn.id, card.id) } : null}
        />
      );
    }
    if (card.kind === "comment") {
      return (
        <CommentCard
          key={card.id}
          comment={card.item}
          status={cs.status}
          locked={running}
          onAdd={(text) => void controller.addComment(turn.id, card.id, text)}
          onDismiss={() => controller.dismiss(turn.id, card.id)}
          onRestore={() => controller.restore(turn.id, card.id)}
          onJump={jump}
        />
      );
    }
    return (
      <InsertCard
        key={card.id}
        insert={card.item}
        status={cs.status}
        locked={running}
        onInsert={(at) => void controller.insert(turn.id, card.id, at)}
        onDismiss={() => controller.dismiss(turn.id, card.id)}
        onRestore={() => controller.restore(turn.id, card.id)}
      />
    );
  };

  return (
    <section className="artifacts" aria-label="Ella’s suggestions">
      {counts.length ? (
        <div className="artifacts__bar">
          <span className="artifacts__count">{counts.join(" · ")}</span>
          {artifacts.edits.length > 1 ? (
            <button
              type="button"
              className="btn btn--sm btn--primary"
              disabled={running || pendingEdits === 0}
              onClick={() => void controller.applyAllTracked(turn.id)}
            >
              {running ? "Applying…" : pendingEdits === 0 ? "All edits handled" : `Apply all ${pendingEdits} as tracked`}
            </button>
          ) : null}
        </div>
      ) : null}
      {applyAll?.summary ? (
        <p className="artifacts__summary" role="status">
          {applyAll.summary}
        </p>
      ) : null}
      {turn.docChanged ? <p className="note">The document changed since Ella read it. Each card re-finds its text before applying.</p> : null}
      {artifacts.truncated ? <p className="note">Ella had more suggestions than one reply can hold. Ask for the rest.</p> : null}

      {grouped.loose.length ? <div className="group">{grouped.loose.map(renderCard)}</div> : null}
      {grouped.groups.map((g) => (
        <div key={g.pid} className="group" data-pid={g.pid}>
          <button type="button" className="group__head" onClick={() => void controller.jump(g.pid)} title="Show this paragraph">
            <span className="group__pid">{g.listString ? g.listString : pidLabel(g.pid)}</span>
            <span className="group__preview">{g.preview}</span>
          </button>
          {g.cards.map(renderCard)}
        </div>
      ))}

      {artifacts.compares.map((c) => (
        <div key={c.compare_id} className="group">
          <CompareCard compare={c} onJump={(pid) => void controller.jump(pid)} />
        </div>
      ))}

      {artifacts.findings.length ? (
        <div className="group">
          <div className="group__title">
            Clean-up check · {artifacts.findings.length} finding{artifacts.findings.length === 1 ? "" : "s"}
          </div>
          {artifacts.findings.map((f) => (
            <FindingCard
              key={f.finding_id}
              finding={f}
              canAsk={!state.busy}
              onJump={(pid) => void controller.jump(pid)}
              onAskFix={() => void controller.send(fixPrompt(f.check, f.message, f.pids))}
            />
          ))}
          <p className="note">These checks can miss problems; no findings is not a clean bill.</p>
        </div>
      ) : null}
    </section>
  );
}

// ---- a turn --------------------------------------------------------------------------------------------------

function TurnView({ controller, state, turn, onRetry, onReconnect }: { controller: ChatController; state: ChatState; turn: Turn; onRetry: () => void; onReconnect?: () => void }) {
  const running = turn.phase !== "done" && turn.phase !== "failed";
  const jump = (pid: string) => void controller.jump(pid);
  return (
    <article className="turn" data-turn={turn.id}>
      <div className="turn__user">{turn.message}</div>
      <div className="turn__ella">
        {running ? (
          <div className="activity" role="status" aria-live="polite">
            <span className="activity__dot" aria-hidden="true" />
            <span>{turn.activity || "Working…"}</span>
          </div>
        ) : null}
        {running && turn.draft ? <Markdown className="md md--draft" text={turn.draft} citations={turn.citations} /> : null}
        {turn.answer ? <Markdown text={turn.answer} onJump={jump} citations={turn.citations} /> : null}
        {turn.degraded ? <p className="note">Ella finished with limited sources this time.</p> : null}
        {turn.citations.some((c) => c.url) ? (
          <details className="sources">
            <summary>Sources ({turn.citations.length})</summary>
            <ol>
              {turn.citations.map((c, i) => (
                <li key={i} value={c.num ?? undefined}>
                  {c.url ? (
                    <a href={c.url} target="_blank" rel="noopener noreferrer">
                      {c.title || c.url}
                    </a>
                  ) : (
                    c.title
                  )}
                  {c.court ? <span className="small"> · {c.court}</span> : null}
                </li>
              ))}
            </ol>
          </details>
        ) : null}
        <ArtifactsView controller={controller} state={state} turn={turn} />
        {turn.phase === "failed" && turn.error ? (
          <div className="error" role="alert">
            <span>{turn.error}</span>
            {turn.authLost && onReconnect ? (
              <button type="button" className="link-btn" onClick={onReconnect}>
                Reconnect
              </button>
            ) : (
              <button type="button" className="link-btn" disabled={state.busy} onClick={onRetry}>
                Try again
              </button>
            )}
          </div>
        ) : null}
      </div>
    </article>
  );
}

function Welcome() {
  return (
    <div className="welcome">
      <h2 className="welcome__title">What should Ella do with this document?</h2>
      <p className="welcome__text">
        Ask a question, review it against your playbook, or have Ella redline a clause. Suggestions arrive as cards you apply as tracked
        changes, one at a time or all at once.
      </p>
    </div>
  );
}

function DebugFooter({ state }: { state: ChatState }) {
  const last = [...state.turns].reverse().find((t) => t.debug !== null);
  const d = last?.debug;
  if (!d) return <div className="debug">debug · no capture yet</div>;
  const kb = (n?: number) => (n === undefined ? "–" : `${(n / 1024).toFixed(1)} KB`);
  return (
    <div className="debug" data-testid="debug-footer">
      {d.paragraphs} ¶ · {d.chars.toLocaleString("en-US")} chars · capture {d.captureMs} ms · {kb(d.rawBytes)} → {kb(d.gzipBytes)} gz · upload{" "}
      {d.uploadMs ?? "–"} ms{d.requestId ? ` · ${d.requestId.slice(0, 8)}` : ""}
      {d.docKeyPersisted === false ? " · doc_key session-only" : ""}
    </div>
  );
}

// ---- panel ---------------------------------------------------------------------------------------------------

export function ChatPanel({
  controller,
  handsReady,
  debug = false,
  onReconnect,
}: {
  controller: ChatController;
  handsReady: boolean;
  debug?: boolean;
  onReconnect?: () => void;
}) {
  const state = useSyncExternalStore(controller.subscribe, controller.getState);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [saving, setSaving] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState<"list" | "pick" | null>(null);
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  const lastTurn = state.turns[state.turns.length - 1];
  const visibleVersions = useMemo(() => controller.visibleVersions(), [controller, state.versions, state.threadId]);

  useEffect(() => {
    void controller.refreshTrackingMode();
    void controller.loadVersions();
    void controller.checkLeftOffTracking();
  }, [controller]);

  async function compareWith(versionId: string): Promise<void> {
    const prompt = await controller.compareWith(versionId);
    if (prompt === null) return;
    setVersionsOpen(null);
    setPrefill((previous) => ({ text: prompt, n: (previous?.n ?? 0) + 1 }));
  }

  async function saveVersion(name: string): Promise<void> {
    const saved = await controller.saveVersion(name);
    if (saved !== null) setSaving(false);
  }

  // New message: follow it to the bottom. Finished: bring the start of Ella's reply into view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el === null || lastTurn === undefined) return;
    const node = el.querySelector<HTMLElement>(`[data-turn="${lastTurn.id}"]`);
    if (lastTurn.phase === "done" || lastTurn.phase === "failed") node?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    else el.scrollTop = el.scrollHeight;
  }, [lastTurn?.id, lastTurn?.phase]);

  const ownComments = state.ellaCommentIds.length;
  const canSave = handsReady && state.threadId !== null && !state.busy && !state.versionBusy;
  return (
    <section className="chat">
      <div className="chat__bar">
        <span className="chat__group">
          <button type="button" className="link-btn" disabled={state.busy || state.turns.length === 0} onClick={() => void controller.newChat()}>
            New chat
          </button>
          <button
            type="button"
            className="link-btn"
            disabled={!canSave || saving}
            title={state.threadId === null ? "Send Ella a message first: a version belongs to a conversation." : "Save the document as it is now"}
            onClick={() => {
              setVersionsOpen(null);
              setSaving(true);
            }}
          >
            Save version
          </button>
          <button
            type="button"
            className="link-btn"
            aria-expanded={versionsOpen !== null}
            onClick={() => {
              setSaving(false);
              setVersionsOpen(versionsOpen === null ? "list" : null);
            }}
          >
            Versions{visibleVersions.length ? ` (${visibleVersions.length})` : ""}
          </button>
        </span>
        {ownComments > 0 ? (
          confirmRemove ? (
            <span className="chat__confirm">
              Remove {ownComments} comment{ownComments === 1 ? "" : "s"} Ella added?
              <button
                type="button"
                className="link-btn link-btn--danger"
                onClick={() => {
                  setConfirmRemove(false);
                  void controller.removeEllaComments();
                }}
              >
                Remove
              </button>
              <button type="button" className="link-btn" onClick={() => setConfirmRemove(false)}>
                Keep
              </button>
            </span>
          ) : (
            <button type="button" className="link-btn" onClick={() => setConfirmRemove(true)}>
              Remove Ella’s comments ({ownComments})
            </button>
          )
        ) : null}
      </div>
      {saving ? (
        <SaveVersionForm
          defaultName={`Version ${visibleVersions.length + 1}`}
          busy={state.versionBusy}
          onSave={(name) => void saveVersion(name)}
          onCancel={() => setSaving(false)}
        />
      ) : null}
      {versionsOpen !== null ? (
        <VersionsMenu
          versions={visibleVersions}
          loaded={state.versionsLoaded}
          busy={state.versionBusy || state.busy}
          picking={versionsOpen === "pick"}
          otherConversation={(v) => v.threadId !== state.threadId}
          onCompare={(id) => void compareWith(id)}
          onDelete={(id) => void controller.deleteVersion(id)}
          onClose={() => setVersionsOpen(null)}
        />
      ) : null}
      {state.trackingLeftOff ? (
        <div className="banner" role="status" data-testid="tracking-left-off">
          <span>
            Ella turned Track Changes on to apply a change and it was not put back. It was {modeLabel(state.trackingLeftOff.previousMode)} before;
            it is {modeLabel(state.trackingLeftOff.currentMode)} now.
          </span>
          <span className="banner__actions">
            <button type="button" className="link-btn" onClick={() => void controller.restoreLeftOffTracking()}>
              Turn it {state.trackingLeftOff.previousMode === "Off" ? "off" : "back"}
            </button>
            <button type="button" className="link-btn" aria-label="Keep Track Changes as it is" onClick={() => controller.dismissLeftOffTracking()}>
              Keep
            </button>
          </span>
        </div>
      ) : null}
      {state.notice ? (
        <div className={state.notice.tone === "error" ? "error" : "banner"} role="status">
          <span>{state.notice.text}</span>
          <button type="button" className="link-btn" aria-label="Dismiss message" onClick={() => controller.dismissNotice()}>
            ×
          </button>
        </div>
      ) : null}
      <div className="chat__scroll" ref={scrollRef}>
        {state.turns.length === 0 ? <Welcome /> : null}
        {state.turns.map((turn) => (
          <TurnView
            key={turn.id}
            controller={controller}
            state={state}
            turn={turn}
            onRetry={() => void controller.send(turn.message)}
            onReconnect={onReconnect}
          />
        ))}
      </div>
      <Composer
        busy={state.busy}
        disabled={!handsReady}
        prefill={prefill}
        onSend={(text) => void controller.send(text)}
        onPickVersion={() => {
          setSaving(false);
          setVersionsOpen("pick");
        }}
      />
      {debug ? <DebugFooter state={state} /> : null}
    </section>
  );
}
